/**
 * 视口监听工具
 * 监听视口变化、导航栏变化，区分视口高度和可视高度
 */

class ViewportMonitor {
  constructor() {
    this.state = {
      viewportHeight: window.innerHeight,        // 视口高度（包括导航栏）
      viewportWidth: window.innerWidth,          // 视口宽度
      visualHeight: document.documentElement.clientHeight,  // 可视高度（不包括导航栏）
      visualWidth: document.documentElement.clientWidth,    // 可视宽度
      navbarHeight: 0,                           // 导航栏高度
      screenHeight: window.screen.height,        // 屏幕高度
      screenWidth: window.screen.width,          // 屏幕宽度
      availHeight: window.screen.availHeight,    // 可用高度
      availWidth: window.screen.availWidth,      // 可用宽度
      isMobile: window.innerWidth <= 734,        // 是否移动设备
      isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream,
      isSafari: /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent),
      virtualKeyboardActive: false,              // 虚拟键盘是否激活
      orientation: window.screen.orientation?.type || 'portrait' // 设备方向
    };
    
    this.previousState = { ...this.state };
    this.isInitialized = false;
    this.resizeTimeout = null;
    this.orientationTimeout = null;
    
    // 绑定方法
    this.handleResize = this.handleResize.bind(this);
    this.handleOrientationChange = this.handleOrientationChange.bind(this);
    this.handleVisualViewportChange = this.handleVisualViewportChange.bind(this);
    this.updateState = this.updateState.bind(this);
    this.calculateNavbarHeight = this.calculateNavbarHeight.bind(this);
    this.detectVirtualKeyboard = this.detectVirtualKeyboard.bind(this);
    
    this.init();
  }

  /**
   * 初始化监听器
   */
  init() {
    if (this.isInitialized) return;
    
    // 初始计算
    this.updateState();
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize);
    
    // 监听设备方向变化
    window.addEventListener('orientationchange', this.handleOrientationChange);
    
    // 监听视觉视口变化（现代浏览器）
    if ('visualViewport' in window) {
      window.visualViewport.addEventListener('resize', this.handleVisualViewportChange);
      window.visualViewport.addEventListener('scroll', this.handleVisualViewportChange);
    }
    
    // 监听屏幕方向变化
    if ('screen' in window && 'orientation' in window.screen) {
      window.screen.orientation.addEventListener('change', this.handleOrientationChange);
    }
    
    this.isInitialized = true;
  }

  /**
   * 处理窗口大小变化
   */
  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.updateState();
    }, 100);
  }

  /**
   * 处理设备方向变化
   */
  handleOrientationChange() {
    clearTimeout(this.orientationTimeout);
    this.orientationTimeout = setTimeout(() => {
      this.updateState();
    }, 300);
  }

  /**
   * 处理视觉视口变化
   */
  handleVisualViewportChange() {
    if ('visualViewport' in window) {
      const vp = window.visualViewport;
      this.state.visualHeight = vp.height;
      this.state.visualWidth = vp.width;
      this.state.viewportHeight = window.innerHeight;
      this.state.viewportWidth = window.innerWidth;
      
      // 重新计算导航栏高度
      this.calculateNavbarHeight();
      
      // 检测虚拟键盘
      this.detectVirtualKeyboard();
      
      this.dispatchChangeEvent('visualViewport');
    }
  }

  /**
   * 更新状态
   */
  updateState() {
    const newState = {
      viewportHeight: window.innerHeight,
      viewportWidth: window.innerWidth,
      visualHeight: document.documentElement.clientHeight,
      visualWidth: document.documentElement.clientWidth,
      screenHeight: window.screen.height,
      screenWidth: window.screen.width,
      availHeight: window.screen.availHeight,
      availWidth: window.screen.availWidth,
      isMobile: window.innerWidth <= 734,
      isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream,
      isSafari: /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent),
      orientation: window.screen.orientation?.type || 'portrait'
    };
    
    // 计算导航栏高度
    this.calculateNavbarHeight();
    
    // 检测虚拟键盘
    this.detectVirtualKeyboard();
    
    // 检查是否有变化
    const hasChanged = this.checkForChanges(newState);
    
    // 更新状态
    this.previousState = { ...this.state };
    this.state = { ...this.state, ...newState };
    
    // 如果有变化，触发事件
    if (hasChanged) {
      this.dispatchChangeEvent('resize');
    }
    
    // 输出调试信息
    this.logState();
  }

  /**
   * 计算导航栏高度
   */
  calculateNavbarHeight() {
    // 方法1：视口高度 - 可视高度
    const method1 = Math.max(0, this.state.viewportHeight - this.state.visualHeight);
    
    // 方法2：屏幕高度 - 可用高度
    const method2 = Math.max(0, this.state.screenHeight - this.state.availHeight);
    
    // 方法3：使用visualViewport（如果可用）
    let method3 = 0;
    if ('visualViewport' in window) {
      const vp = window.visualViewport;
      method3 = Math.max(0, window.innerHeight - vp.height);
    }
    
    // 选择最合理的方法
    this.state.navbarHeight = method1;
    
    // 记录所有方法的结果
    this.state.navbarHeightMethods = {
      viewportMinusVisual: method1,
      screenMinusAvail: method2,
      visualViewport: method3
    };
  }

  /**
   * 检测虚拟键盘
   */
  detectVirtualKeyboard() {
    if (this.state.isMobile) {
      const screenHeight = this.state.screenHeight;
      const viewportRatio = this.state.viewportHeight / screenHeight;
      const visualRatio = this.state.visualHeight / screenHeight;
      
      // 虚拟键盘激活的判断条件
      const isActive = viewportRatio < 0.8 || visualRatio < 0.8;
      
      this.state.virtualKeyboardActive = isActive;
      this.state.virtualKeyboardHeight = isActive ? 
        Math.max(0, screenHeight - this.state.viewportHeight) : 0;
      this.state.viewportRatio = viewportRatio;
      this.state.visualRatio = visualRatio;
    } else {
      this.state.virtualKeyboardActive = false;
      this.state.virtualKeyboardHeight = 0;
      this.state.viewportRatio = 1;
      this.state.visualRatio = 1;
    }
  }

  /**
   * 检查状态变化
   */
  checkForChanges(newState) {
    const keys = [
      'viewportHeight', 'viewportWidth', 'visualHeight', 'visualWidth',
      'navbarHeight', 'virtualKeyboardActive', 'orientation'
    ];
    
    return keys.some(key => this.state[key] !== newState[key]);
  }

  /**
   * 触发变化事件
   */
  dispatchChangeEvent(type) {
    const event = new CustomEvent('viewportChange', {
      detail: {
        type: type,
        current: { ...this.state },
        previous: { ...this.previousState },
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(event);
  }

  /**
   * 输出状态信息
   */
  logState() {
    // 状态日志功能已移除
  }

  /**
   * 获取当前状态
   */
  getState() {
    return { ...this.state };
  }

  /**
   * 获取导航栏高度
   */
  getNavbarHeight() {
    return this.state.navbarHeight;
  }

  /**
   * 获取可视高度
   */
  getVisualHeight() {
    return this.state.visualHeight;
  }

  /**
   * 获取视口高度
   */
  getViewportHeight() {
    return this.state.viewportHeight;
  }

  /**
   * 检查虚拟键盘是否激活
   */
  isVirtualKeyboardActive() {
    return this.state.virtualKeyboardActive;
  }

  /**
   * 获取虚拟键盘高度
   */
  getVirtualKeyboardHeight() {
    return this.state.virtualKeyboardHeight;
  }

  /**
   * 销毁监听器
   */
  destroy() {
    if (!this.isInitialized) return;
    
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleOrientationChange);
    
    if ('visualViewport' in window) {
      window.visualViewport.removeEventListener('resize', this.handleVisualViewportChange);
      window.visualViewport.removeEventListener('scroll', this.handleVisualViewportChange);
    }
    
    if ('screen' in window && 'orientation' in window.screen) {
      window.screen.orientation.removeEventListener('change', this.handleOrientationChange);
    }
    
    clearTimeout(this.resizeTimeout);
    clearTimeout(this.orientationTimeout);
    
    this.isInitialized = false;
  }
}

// 创建全局实例
window.viewportMonitor = new ViewportMonitor();

// 导出模块
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ViewportMonitor;
} 